import { Injectable, inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageService } from 'primeng/api';
import { VALIDATION_ERROR_CONFIG, ValidationErrorStrategy } from '@config/app.config';

/**
 * Serwis obsługujący błędy walidacji z backendu
 * 
 * Odpowiedzialności:
 * - Pokazuje toast z błędami walidacji (wywoływany przez interceptor)
 * 
 * Field errors (błędy przy polach) są obsługiwane przez:
 * - FormErrorMsgComponent z @Input backendErrors
 */
@Injectable({ providedIn: 'root' })
export class ValidationErrorHandlerService {
  private transloco = inject(TranslocoService);
  private messageService = inject(MessageService);
  
  /**
   * Obsłuż błąd walidacji - pokaż toast ze szczegółami
   * Wywoływany automatycznie przez HttpErrorInterceptor
   * 
   * @param error - Błąd z backendu (format: { errorCode, validationErrors, data })
   */
  handleValidationError(error: any): void {
    if (!this.isValidationError(error)) {
      return;
    }
    
    // NIE pokazuj toast jeśli strategia to FIELD_ONLY
    if (VALIDATION_ERROR_CONFIG.strategy === ValidationErrorStrategy.FIELD_ONLY) {
      return;
    }
    
    this.showToast(error);
  }
  
  /**
   * Sprawdź czy to błąd walidacji z backendu
   */
  private isValidationError(error: any): boolean {
    return error?.errorCode === 'VALIDATION.MULTIPLE_ERRORS' 
      && !!error?.validationErrors;
  }
  
  /**
   * Pokaż toast ze szczegółami błędów
   */
  private showToast(error: any): void {
    const allErrors = this.collectAllErrors(error);
    const detail = allErrors.join(VALIDATION_ERROR_CONFIG.toastErrorsSeparator);
    
    this.messageService.add({
      ...VALIDATION_ERROR_CONFIG.toast,
      summary: this.transloco.translate('errors.VALIDATION.TITLE'),
      detail,
      contentStyleClass: 'white-space-pre-line'
    });
  }
  
  /**
   * Zbierz wszystkie błędy jako string[] (pole: komunikat)
   */
  private collectAllErrors(error: any): string[] {
    if (!error.validationErrors) {
      return [];
    }
    
    return Object.entries(error.validationErrors).flatMap(([field, codes]) => {
      const fieldLabel = this.transloco.translate(`fields.${field}`);
      const messages = this.translateErrors(codes as string[], error.data?.[field]);
      
      return messages.map(msg => `${fieldLabel}: ${msg}`);
    });
  }
  
  /**
   * Tłumacz kody błędów na komunikaty
   */
  private translateErrors(codes: string[], data?: any): string[] {
    return codes.map(code => 
      this.transloco.translate(
        `${VALIDATION_ERROR_CONFIG.errorTranslationPrefix}.${code}`, 
        data || {}
      )
    );
  }
}
